import React from "react";
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import { 
  Upload, 
  Plus, 
  X, 
  Play, 
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Download
} from 'lucide-react'
import { startBatchScan, getBatchStatus } from '../services/api'
import type { BatchScanRequest, BatchScanResponse } from '../../../shared/types'

export default function BatchScanPage() {
  const [urls, setUrls] = useState<string[]>([''])
  const [batchId, setBatchId] = useState<string | null>(null)
  const [wcagLevel, setWcagLevel] = useState<'A' | 'AA' | 'AAA'>('AA')
  
  const addUrl = () => {
    setUrls([...urls, ''])
  }
  
  const removeUrl = (index: number) => {
    setUrls(urls.filter((_, i) => i !== index))
  }
  
  const updateUrl = (index: number, value: string) => {
    const newUrls = [...urls]
    newUrls[index] = value
    setUrls(newUrls)
  }
  
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      const uploadedUrls = text
        .split('\n')
        .map(url => url.trim())
        .filter(url => url && url.startsWith('http'))
      setUrls(uploadedUrls)
      toast.success(`Loaded ${uploadedUrls.length} URLs from file`)
    }
    reader.readAsText(file)
  }
  
  const batchMutation = useMutation({
    mutationFn: startBatchScan,
    onSuccess: (data) => {
      setBatchId(data.batchId)
      toast.success('Batch scan started')
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to start batch scan')
    },
  })
  
  const { data: batchStatus } = useQuery<BatchScanResponse>({
    queryKey: ['batch', batchId],
    queryFn: () => getBatchStatus(batchId!),
    enabled: !!batchId,
    refetchInterval: (data) => {
      if (data?.status === 'completed' || data?.status === 'failed') {
        return false
      }
      return 3000
    },
  })
  
  const startBatch = () => {
    const validUrls = urls.filter(url => url && url.startsWith('http'))
    if (validUrls.length === 0) {
      toast.error('Please add at least one valid URL')
      return
    }
    
    const request: BatchScanRequest = {
      urls: validUrls,
      options: {
        url: '',
        wcagLevel,
        includeWarnings: true,
      },
      priority: 'medium',
    }
    
    batchMutation.mutate(request)
  }
  
  const exportUrls = () => {
    const validUrls = urls.filter(url => url && url.startsWith('http'))
    const blob = new Blob([validUrls.join('\n')], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'urls.txt'
    a.click()
    URL.revokeObjectURL(url)
  }
  
  const progressPercentage = batchStatus
    ? Math.round(((batchStatus.progress.completed + batchStatus.progress.failed) / batchStatus.totalUrls) * 100)
    : 0

  return (
    <>
      <Head>
        <title>Batch Scan - Accessibility Checker</title>
        <meta name="description" content="Scan multiple URLs for accessibility issues" />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={getCanonicalUrl('/batch')} />
      </Head>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Batch Accessibility Testing
        </h1>
        <p className="text-gray-600">
          Test multiple URLs at once and get comprehensive accessibility reports
        </p>
      </div>

      {/* URL Input Section */}
      {!batchId && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">URLs to Test</h2>
              <div className="flex gap-2">
                <label className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer flex items-center gap-2 text-sm">
                  <Upload className="w-4 h-4" />
                  Upload File
                  <input
                    type="file"
                    accept=".txt,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={exportUrls}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>

            {/* URL List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {urls.map((url, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => updateUrl(index, e.target.value)}
                    placeholder="https://example.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => removeUrl(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                    disabled={urls.length === 1}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={addUrl}
              className="mt-3 flex items-center gap-2 text-blue-600 hover:text-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add Another URL
            </button>
          </div>

          {/* Settings */}
          <div className="border-t pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              WCAG Compliance Level
            </label>
            <div className="grid grid-cols-3 gap-3">
              {(['A', 'AA', 'AAA'] as const).map((level) => (
                <label
                  key={level}
                  className={`
                    relative flex items-center justify-center p-3 border rounded-lg cursor-pointer transition-colors
                    ${wcagLevel === level
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 hover:bg-gray-50'
                    }
                  `}
                >
                  <input
                    type="radio"
                    value={level}
                    checked={wcagLevel === level}
                    onChange={() => setWcagLevel(level)}
                    className="sr-only"
                  />
                  <span className="font-medium">Level {level}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={startBatch}
            disabled={batchMutation.isLoading}
            className="mt-6 w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {batchMutation.isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Starting Batch Scan...
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Start Batch Scan ({urls.filter(u => u).length} URLs)
              </>
            )}
          </button>
        </div>
      )}

      {/* Progress Section */}
      {batchStatus && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Scan Progress</h2>
              <span className="text-sm text-gray-600">
                {progressPercentage}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Status Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Completed</span>
              </div>
              <div className="text-2xl font-bold text-green-900">
                {batchStatus.progress.completed}
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-700 mb-1">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">In Progress</span>
              </div>
              <div className="text-2xl font-bold text-yellow-900">
                {batchStatus.progress.pending}
              </div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-700 mb-1">
                <XCircle className="w-5 h-5" />
                <span className="font-medium">Failed</span>
              </div>
              <div className="text-2xl font-bold text-red-900">
                {batchStatus.progress.failed}
              </div>
            </div>
          </div>

          {/* Results List */}
          {batchStatus.results && batchStatus.results.length > 0 && (
            <div className="border-t pt-6">
              <h3 className="font-medium text-gray-900 mb-3">Results</h3>
              <div className="space-y-2">
                {batchStatus.results.map((result, index) => (
                  <div
                    key={result.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {result.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : result.status === 'failed' ? (
                        <XCircle className="w-5 h-5 text-red-500" />
                      ) : (
                        <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                      )}
                      <span className="text-sm text-gray-700">
                        URL {index + 1}
                      </span>
                    </div>
                    {result.reportUrl && (
                      <a
                        href={result.reportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                      >
                        <FileText className="w-4 h-4" />
                        View Report
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completion Actions */}
          {batchStatus.status === 'completed' && (
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => window.location.href = '/reports'}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                View All Reports
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Start New Batch
              </button>
            </div>
          )}
        </div>
      )}
    </div>
    </>
  )
}
